import { useCallback, useMemo } from 'react';
import { Panel } from '@/components/ui';
import { DealPinMap } from '@/components/viz';
import { type Deal } from '@/types';
import { formatCompactCurrency } from '@/lib/format';
import { parseBasisUsd } from '@/lib/pipeline-irr';

interface PipelineMapProps {
  deals: Deal[];
  isLoading: boolean;
  error: Error | null;
}

// parseBasisUsd is now the single canonical implementation at lib/pipeline-irr.ts
// (conversation-model-versioning Phase 5 Session 9 — lifted from this file to
// remove the module-local duplicate; §13.C basis-parser footgun guard).

/**
 * Cockpit-side Pipeline Map wrapper around `<DealPinMap>`. Renders a Panel
 * with title "Pipeline Map" + LIVE badge, embeds the shared map primitive
 * with `footerVariant="stat-row"` and a single-line stat-row footer node
 * (`N DEALS · $XM PIPELINE`).
 *
 * Bidirectional hover (cockpit pairing with `<PipelineSummary>`):
 *  - Subscribes to `arcanum:pipeline-row-highlight` events via the embedded
 *    `<DealPinMap>`'s `subscribeToHighlightEvents={true}` prop.
 *  - Emits the same event on pin hover via `onPinHover` → `dispatchEvent`
 *    with `source: 'pin'`. Self-source guard inside `<DealPinMap>` ignores
 *    `source==='pin'` events, so this wrapper doesn't echo back to itself.
 *
 * Stat-row footer uses single-line variant per architect decision R4
 * (design.md). `formatCompactCurrency` returns `$XM` / `$XB` / `$XK` per
 * research § 4.6 verbatim. No try/catch wrap on `dispatchEvent` per
 * research § 4.10 — `window.dispatchEvent` is non-throwing in standards
 * browsers.
 */
export function PipelineMap({ deals, isLoading, error }: PipelineMapProps) {
  const statRowText = useMemo(() => {
    if (deals.length === 0) return '0 DEALS · No pipeline yet';
    const totalCap = deals.reduce((acc, d) => acc + parseBasisUsd(d.basis), 0);
    return `${deals.length} DEAL${
      deals.length === 1 ? '' : 'S'
    } · ${formatCompactCurrency(totalCap)} PIPELINE`;
  }, [deals]);

  const handlePinHover = useCallback((dealId: string | null) => {
    window.dispatchEvent(
      new CustomEvent('arcanum:pipeline-row-highlight', {
        detail: { dealId, source: 'pin' },
      })
    );
  }, []);

  return (
    <Panel title="Pipeline Map" badge="LIVE" className="h-full">
      <DealPinMap
        deals={deals}
        isLoading={isLoading}
        error={error}
        subscribeToHighlightEvents={true}
        onPinHover={handlePinHover}
        emptyVariant="bare"
        footerVariant="stat-row"
        footerNode={
          <div className="px-2 py-1 rounded-[2px] border border-panel-border bg-input-bg flex items-center justify-center">
            <span className="font-mono font-bold text-[10px] tracking-[0.4px] text-text-bright">
              {statRowText}
            </span>
          </div>
        }
      />
    </Panel>
  );
}
